import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Banknote, Smartphone, Wallet, ArrowLeft, Lock, RotateCcw, Tag, TicketPercent, X } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";
import api, { inr, formatApiErrorDetail, FREE_SHIPPING_ABOVE, SHIPPING_FLAT } from "@/lib/api";
import Reveal from "@/components/Reveal";

const fields = [
  { name: "name", label: "Full name", type: "text", ph: "Aarav Sharma" },
  { name: "phone", label: "Phone", type: "tel", ph: "98765 43210" },
  { name: "email", label: "Email", type: "email", ph: "you@example.com" },
  { name: "address", label: "Address", type: "text", ph: "Flat, street, landmark" },
  { name: "city", label: "City", type: "text", ph: "Mumbai" },
  { name: "state", label: "State", type: "text", ph: "Maharashtra" },
  { name: "pincode", label: "Pincode", type: "text", ph: "400001" },
];

const loadRazorpay = () =>
  new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

export default function Checkout() {
  const { items, subtotal, distinct, bundleRate, bundleDiscount, coupon, couponDef, couponDiscount, shippingWaived, applyCoupon, removeCoupon, discount, clear } = useCart();
  const navigate = useNavigate();
  const [payment, setPayment] = useState("cod");
  const [loading, setLoading] = useState(false);
  const [retryOrder, setRetryOrder] = useState(null);

  const { data: config } = useQuery({
    queryKey: ["config"],
    queryFn: async () => (await api.get("/config")).data,
  });
  const razorpayEnabled = !!config?.razorpay;

  const shipping = shippingWaived || subtotal >= FREE_SHIPPING_ABOVE ? 0 : subtotal > 0 ? SHIPPING_FLAT : 0;
  const total = subtotal - discount + shipping;

  if (items.length === 0 && !retryOrder) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 px-4 pt-16 text-center" data-testid="checkout-empty-state">
        <p className="font-display text-4xl font-extrabold text-forest">Nothing to check out.</p>
        <p className="text-sm text-ink/60">Your cart is feeling lonely. Fix that.</p>
        <Link to="/shop" data-testid="checkout-empty-shop-link" className="rounded-full bg-forest px-6 py-3 text-sm font-bold text-cream hover:bg-leaf">Shop the powders</Link>
      </div>
    );
  }

  const openRazorpay = async (orderId) => {
    const loaded = await loadRazorpay();
    if (!loaded) {
      toast.error("Couldn't load payments. Check your connection or use Cash on Delivery.");
      setLoading(false);
      return;
    }
    try {
      const pay = await api.post("/payments/create", { order_id: orderId });
      const rzp = new window.Razorpay({
        key: pay.data.key_id,
        amount: pay.data.amount,
        currency: "INR",
        name: "Abhivesha Foods",
        description: `Order ${orderId}`,
        order_id: pay.data.razorpay_order_id,
        prefill: pay.data.prefill,
        theme: { color: "#0D3220" },
        handler: async (r) => {
          try {
            await api.post("/payments/verify", { order_id: orderId, razorpay_order_id: r.razorpay_order_id, razorpay_payment_id: r.razorpay_payment_id, razorpay_signature: r.razorpay_signature });
            clear();
            setRetryOrder(null);
            navigate(`/order/${orderId}`);
          } catch {
            toast.error("Payment succeeded but verification failed. We'll confirm manually.");
            setLoading(false);
          }
        },
        modal: {
          ondismiss: () => {
            toast.info("Payment window closed — your order is saved. Retry anytime below.");
            setLoading(false);
            setRetryOrder(orderId);
          },
        },
      });
      rzp.open();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't start payment. Try COD instead.");
      setLoading(false);
    }
  };

  const placeOrder = async (e) => {
    e.preventDefault();
    const data = Object.fromEntries(new FormData(e.currentTarget).entries());
    setLoading(true);
    try {
      const res = await api.post("/orders", {
        customer: data,
        items: items.map((i) => ({ slug: i.slug, qty: i.qty })),
        payment_method: payment,
        coupon_code: coupon,
      });
      if (payment === "razorpay") {
        await openRazorpay(res.data.order_id);
      } else {
        clear();
        navigate(`/order/${res.data.order_id}`);
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Order failed. Check your details and try again.");
      setLoading(false);
    }
  };

  const paymentMethods = [
    { id: "cod", icon: Banknote, t: "Cash on Delivery", d: "Pay when the powders reach your door." },
    { id: "upi", icon: Smartphone, t: "UPI on confirmation", d: "We confirm your order on call/WhatsApp, then you pay by UPI." },
  ];
  if (razorpayEnabled) {
    paymentMethods.unshift({ id: "razorpay", icon: Wallet, t: "Pay online", d: "UPI, cards, netbanking & wallets via Razorpay. Instant confirmation." });
  }

  return (
    <div className="mx-auto max-w-6xl px-4 pb-24 pt-28 sm:px-6" data-testid="checkout-page">
      <Reveal>
        <Link to="/shop" data-testid="checkout-back-link" className="inline-flex items-center gap-1.5 font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-ink/50 hover:text-leaf">
          <ArrowLeft className="h-3.5 w-3.5" /> Keep shopping
        </Link>
        <h1 className="mt-3 font-display text-4xl font-extrabold tracking-tight text-forest sm:text-5xl">CHECKOUT.</h1>
        <p className="mt-2 font-hand text-2xl text-forest/60">almost at the good part.</p>
      </Reveal>

      {retryOrder && (
        <div className="mt-6 flex flex-wrap items-center justify-between gap-3 rounded-2xl border-2 border-leaf/40 bg-leaf/10 p-4" data-testid="retry-payment-panel">
          <p className="text-sm font-bold text-forest">Order <span className="font-mono">{retryOrder}</span> is saved — payment pending.</p>
          <button
            data-testid="retry-payment-button"
            onClick={() => { setLoading(true); openRazorpay(retryOrder); }}
            className="flex items-center gap-2 rounded-full bg-leaf px-5 py-2.5 text-xs font-extrabold uppercase tracking-wide text-white hover:bg-forest"
          >
            <RotateCcw className="h-3.5 w-3.5" /> Retry payment
          </button>
        </div>
      )}

      <div className="mt-10 grid gap-10 lg:grid-cols-[1.3fr,1fr]">
        <form onSubmit={placeOrder} data-testid="checkout-form">
          <div className="rounded-3xl border border-sand bg-white p-6 sm:p-8">
            <h2 className="font-display text-lg font-extrabold uppercase tracking-wide text-forest">Delivery details</h2>
            <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
              {fields.map((f) => (
                <label key={f.name} className={f.name === "address" ? "sm:col-span-2" : ""}>
                  <span className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-ink/60">{f.label}</span>
                  <input
                    data-testid={`checkout-field-${f.name}`}
                    name={f.name}
                    type={f.type}
                    required
                    placeholder={f.ph}
                    pattern={f.name === "phone" ? "[0-9+ ]{10,15}" : undefined}
                    className="w-full rounded-xl border border-sand bg-cream/50 px-4 py-3 text-sm font-semibold text-ink placeholder:text-ink/30 focus:border-leaf focus:bg-white"
                  />
                </label>
              ))}
            </div>
          </div>

          <div className="mt-6 rounded-3xl border border-sand bg-white p-6 sm:p-8">
            <h2 className="font-display text-lg font-extrabold uppercase tracking-wide text-forest">Payment</h2>
            <div className="mt-5 space-y-3">
              {paymentMethods.map((m) => (
                <button
                  type="button"
                  key={m.id}
                  data-testid={`checkout-payment-${m.id}`}
                  onClick={() => setPayment(m.id)}
                  className={`flex w-full items-center gap-4 rounded-2xl border-2 p-4 text-left transition-all ${
                    payment === m.id ? "border-leaf bg-leaf/5" : "border-sand bg-cream/50 hover:border-forest/20"
                  }`}
                >
                  <m.icon className={`h-5 w-5 shrink-0 ${payment === m.id ? "text-leaf" : "text-ink/40"}`} />
                  <span>
                    <span className="block text-sm font-extrabold text-forest">{m.t}</span>
                    <span className="block text-xs text-ink/60">{m.d}</span>
                  </span>
                </button>
              ))}
            </div>
            <label className="mt-5 flex items-start gap-2.5 text-xs leading-relaxed text-ink/60">
              <input
                data-testid="checkout-consent"
                name="consent"
                type="checkbox"
                required
                className="mt-0.5 h-4 w-4 shrink-0 accent-[#12B76A]"
              />
              <span>
                I agree to the{" "}
                <Link to="/terms" className="font-bold text-leaf underline">Terms of Use</Link>,{" "}
                <Link to="/privacy" className="font-bold text-leaf underline">Privacy Policy</Link> and{" "}
                <Link to="/refund-policy" className="font-bold text-leaf underline">Returns &amp; Refund Policy</Link>.
              </span>
            </label>
            <button
              type="submit"
              data-testid="checkout-place-order"
              disabled={loading}
              className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-forest py-4 text-sm font-extrabold uppercase tracking-wide text-cream transition-all hover:bg-leaf active:scale-[0.98] disabled:opacity-60"
            >
              {loading ? <span className="h-4 w-4 animate-spin rounded-full border-2 border-cream/40 border-t-cream" /> : <Lock className="h-4 w-4" />}
              {loading ? "Placing order..." : payment === "razorpay" ? `Pay ${inr(total)} securely` : `Place order · ${inr(total)}`}
            </button>
          </div>
        </form>

        <aside className="lg:sticky lg:top-24 lg:self-start">
          <div className="rounded-3xl border border-sand bg-white p-6 sm:p-8" data-testid="checkout-summary">
            <h2 className="font-display text-lg font-extrabold uppercase tracking-wide text-forest">Order summary</h2>
            <ul className="mt-5 space-y-4">
              {items.map((i) => (
                <li key={i.slug} className="flex items-center gap-3">
                  <div className="relative">
                    <img src={i.image} alt={i.name} className="h-14 w-14 rounded-xl object-cover" />
                    <span className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-forest font-mono text-[10px] font-bold text-cream">{i.qty}</span>
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-extrabold text-forest">{i.name}</p>
                    <p className="text-[11px] font-semibold uppercase tracking-wide" style={{ color: i.accent }}>{i.mascot}</p>
                  </div>
                  <p className="font-mono text-sm font-bold text-forest">{inr(i.price * i.qty)}</p>
                </li>
              ))}
            </ul>

            {coupon ? (
              <div className="mt-5 flex items-center justify-between rounded-xl border border-leaf/30 bg-leaf/10 px-4 py-2.5" data-testid="checkout-coupon-applied">
                <p className="flex items-center gap-2 text-xs font-bold text-leaf"><TicketPercent className="h-4 w-4" /> {coupon} · {couponDef?.label}</p>
                <button data-testid="checkout-coupon-remove" onClick={removeCoupon} className="text-[11px] font-bold uppercase text-ink/50 hover:text-red-500">Remove</button>
              </div>
            ) : (
              <form
                className="mt-5 flex gap-2"
                onSubmit={(e) => { e.preventDefault(); const err = applyCoupon(e.currentTarget.elements.code.value); if (err) toast.error(err); e.currentTarget.reset(); }}
              >
                <div className="relative flex-1">
                  <Tag className="absolute left-3.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-ink/40" />
                  <input
                    data-testid="checkout-coupon-input"
                    name="code"
                    placeholder="Coupon code"
                    className="w-full rounded-xl border border-sand bg-cream/50 py-3 pl-9 pr-3 text-xs font-bold uppercase tracking-wide text-ink placeholder:normal-case placeholder:font-semibold placeholder:text-ink/30 focus:border-leaf focus:bg-white"
                  />
                </div>
                <button data-testid="checkout-coupon-apply" className="rounded-xl bg-forest px-4 text-xs font-extrabold uppercase tracking-wide text-cream hover:bg-leaf">Apply</button>
              </form>
            )}

            <div className="mt-6 space-y-2 border-t border-sand pt-5 text-sm">
              <p className="flex justify-between text-ink/60"><span>Subtotal</span><span className="font-bold text-forest">{inr(subtotal)}</span></p>
              {bundleDiscount > 0 && (
                <p className="flex justify-between text-leaf" data-testid="checkout-bundle-discount">
                  <span className="font-semibold">Bundle saving ({bundleRate}%)</span><span className="font-bold">-{inr(bundleDiscount)}</span>
                </p>
              )}
              {couponDiscount > 0 && (
                <p className="flex justify-between text-leaf" data-testid="checkout-coupon-discount-row">
                  <span className="font-semibold">{coupon}</span><span className="font-bold">-{inr(couponDiscount)}</span>
                </p>
              )}
              {distinct < 3 && !coupon && <p className="rounded-lg bg-leaf/10 px-3 py-2 text-[11px] font-bold text-leaf">Pick any 3 powders to auto-save 10%</p>}
              <p className="flex justify-between text-ink/60"><span>Shipping</span><span data-testid="checkout-shipping" className="font-bold text-forest">{shipping === 0 ? "Free" : inr(shipping)}</span></p>
              <p className="flex justify-between border-t border-sand pt-3 font-display text-xl font-extrabold text-forest"><span>Total</span><span data-testid="checkout-total">{inr(total)}</span></p>
            </div>
            {shipping > 0 && !shippingWaived && <p className="mt-3 rounded-xl bg-leaf/10 px-4 py-2.5 text-xs font-bold text-leaf">Add {inr(FREE_SHIPPING_ABOVE - subtotal)} more for free shipping</p>}
          </div>
        </aside>
      </div>
    </div>
  );
}
